import { darkenHexColor, hexToCssFilter, mixWithWhite } from './colorUtils';

/**
 * The stylesheet a client-picked accent gets instead of `/skin/skin.<id>.css` +
 * `/auth-skin/skin.<id>.css`.
 *
 * Those files are per-skin snapshots: the accent, a handful of tints derived
 * from it, and the `filter` chain that repaints the flat <img> icons. A custom
 * hex has no snapshot on disk, so we generate the same rules from the seed. Only
 * the accent-derived tokens are emitted — greys and the semantic colours are
 * identical in every skin and already come from `styles/utilities/colors.css`.
 *
 * Everything else about a custom theme (the kit's `--mk-brand-*` ramp) is the
 * kit's job via `applyCustomTheme`; see SkinProvider.
 */

/** How much white each accent-derived token carries. */
const TINTS = {
  secondary: 0.88,
  secondaryOpacity: 0.94,
  primaryOpacity: 0.75,
  lightPrimary: 0.9,
  lightPrimary2: 0.78,
  lightPrimary3: 0.85,
  lightPrimary4: 0.92,
  secondPrimary: 0.52,
} as const;

/** Selectors the hand-authored skins repaint with the accent filter. */
const ACCENT_ICON_SELECTORS = [
  '.minthr-header .minthr-header-user-dropdown .user-dropdown-nav > li > a:hover > img',
  'img[class^="minthr-icon"]',
  'div[class^="minthr-icon"] img',
  "[class*='minthr-btn-outline'] > img",
  '.show-password',
  '.minthr-sidenav > ul > li:not(.active):hover > a > .minthr-sidenav-item-icon > img',
  '.minthr-sidenav > ul > li.open > a > .minthr-sidenav-item-icon > img',
  '.minthr-btn-primary:hover img',
  '.minthr-btn-secondary img',
  '.minthr-hover-link-opacity img',
  '.minthr-btn-primary.active img',
  '.minthr-sidenav .minthr-sidenav-submenu > ul > li > a::before',
];

/** Same repaint, but the legacy sheets mark these `!important`. */
const ACCENT_ICON_SELECTORS_IMPORTANT = [
  '.minthr-btn-icon-origin > img',
  '.minthr-chackbox-rounded input:not(:checked) ~ span:after',
  '.minthr-btn-checkbox input:checked ~ span img',
  '.minthr-documents-add-actions button img',
  '.minthr-table-custom-pagination .dataTables_wrapper .dataTables_paginate .paginate_button:not(.current)',
  '.minthr-searchbar:not(.minthr-nav-search) .search-clear',
  '.minthr-searchbar:not(.minthr-nav-search) .search-icon',
];

/** `--minthr-color-danger`, fixed across every skin. */
const DANGER = '#f26c6d';

const rgbaFromHex = (hex: string, alpha: number): string => {
  const raw = hex.replace(/^#/, '');
  const channel = (index: number): number =>
    parseInt(raw.substring(index * 2, index * 2 + 2), 16);
  return `rgba(${channel(0)}, ${channel(1)}, ${channel(2)}, ${alpha})`;
};

/**
 * @param primary a normalized `#rrggbb` accent — see `resolveSkinPrimary`.
 */
export const buildCustomSkinCss = (primary: string): string => {
  const accentFilter = hexToCssFilter(primary);
  const dangerFilter = hexToCssFilter(DANGER);

  const secondary = mixWithWhite(primary, TINTS.secondary);
  const secondaryOpacity = mixWithWhite(primary, TINTS.secondaryOpacity);
  const primaryOpacity = mixWithWhite(primary, TINTS.primaryOpacity);
  const lightPrimary = mixWithWhite(primary, TINTS.lightPrimary);
  const lightPrimary2 = mixWithWhite(primary, TINTS.lightPrimary2);
  const lightPrimary3 = mixWithWhite(primary, TINTS.lightPrimary3);
  const lightPrimary4 = mixWithWhite(primary, TINTS.lightPrimary4);
  const secondPrimary = mixWithWhite(primary, TINTS.secondPrimary);
  const darkPrimary = darkenHexColor(primary, 30);

  return `:root {
  --minthr-color-primary: ${primary};
  --minthr-color-second-primary: ${secondPrimary};
  --minthr-color-dark-primary: ${darkPrimary};
  --minthr-color-secondary: ${secondary};
  --minthr-color-primary-opacity: ${primaryOpacity};
  --minthr-color-secondary-opacity: ${secondaryOpacity};
  --minthr-color-light-primary: ${lightPrimary};
  --minthr-color-light-primary2: ${lightPrimary2};
  --minthr-color-light-primary3: ${lightPrimary3};
  --minthr-color-light-primary4: ${lightPrimary4};

  --color-primary: ${primary};
  --color-secondary: ${secondary};
  --color-primary-opacity: ${primaryOpacity};
  --color-secondary-opacity: ${secondaryOpacity};
  --color-light-primary: ${lightPrimary};
}
${ACCENT_ICON_SELECTORS.join(',\n')} {
  filter: ${accentFilter};
}
${ACCENT_ICON_SELECTORS_IMPORTANT.join(',\n')} {
  filter: ${accentFilter} !important;
}
.minthr-btn-primary:not(.minthr-btn-outline) img,
.minthr-btn-danger:not(.minthr-btn-outline) img {
  filter: brightness(0) invert(1) !important;
}
.minthr-icon-danger {
  filter: ${dangerFilter} !important;
}
.minthr-sidebar-close {
  filter: brightness(1.92) grayscale(1) !important;
}
.minthr-filter-none {
  filter: none !important;
}
.minthr-filter-h-none:hover {
  filter: none !important;
}
.fc-non-business {
  background-color: ${rgbaFromHex(mixWithWhite(primary, 0.45), 0.09)} !important;
}
`;
};
